import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Map as ImmutableMap } from 'immutable';
import { paymentsTotalByStateSelector } from 'domain/payments';
import type { PaymentFlowState } from 'domain/payments/types';

import Tabs from 'components/Tabs';
import Box from '@mui/material/Box';

const mapStateToProps = (state: unknown) => ({
  totalByState: paymentsTotalByStateSelector(state),
});

export const flowStates = {
  paymentPreparation: 'paymentPreparation',
  pendingApproval: 'pendingApproval',
  approved: 'approved',
  inProcess: 'inProcess',
  paid: 'paid',
  all: 'all',
} as const;

const getMessageValue = (totalByState: ImmutableMap<PaymentFlowState, number>, state: PaymentFlowState) => ({ count: totalByState.get(state) || 0 });

type PaymentFlowStatesProps = {
  setShowGrid: (show: boolean) => void;
};

const PaymentFlowStates: React.FC<PaymentFlowStatesProps> = ({ setShowGrid }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const flowState = new URLSearchParams(search).get('flowState');
  const { category1 } = useParams();

  const { totalByState } = useSelector(mapStateToProps);

  const tabs = useMemo(
    () => [
      {
        id: 'paymentWorkspace.flowStates.paymentPreparation',
        defaultMessage: 'Payment preparation ({count})',
        messageValues: getMessageValue(totalByState, flowStates.paymentPreparation),
        to: `?flowState=${flowStates.paymentPreparation}`,
        disabled: false,
        tabValue: flowStates.paymentPreparation,
      },
      {
        id: 'paymentWorkspace.flowStates.pendingApproval',
        defaultMessage: 'Pending approval ({count})',
        messageValues: getMessageValue(totalByState, flowStates.pendingApproval),
        to: `?flowState=${flowStates.pendingApproval}`,
        disabled: false,
        tabValue: flowStates.pendingApproval,
      },
      {
        id: 'paymentWorkspace.flowStates.approved',
        defaultMessage: 'Approved ({count})',
        messageValues: getMessageValue(totalByState, flowStates.approved),
        to: `?flowState=${flowStates.approved}`,
        disabled: false,
        tabValue: flowStates.approved,
      },
      {
        id: 'paymentWorkspace.flowStates.inProcess',
        defaultMessage: 'Payment in process ({count})',
        messageValues: getMessageValue(totalByState, flowStates.inProcess),
        to: `?flowState=${flowStates.inProcess}`,
        disabled: false,
        tabValue: flowStates.inProcess,
      },
      {
        id: 'paymentWorkspace.flowStates.paid',
        defaultMessage: 'Paid ({count})',
        messageValues: getMessageValue(totalByState, flowStates.paid),
        to: `?flowState=${flowStates.paid}`,
        disabled: false,
        tabValue: flowStates.paid,
      },
      {
        id: 'configurations.sidebar.all',
        defaultMessage: 'All ({count})',
        messageValues: getMessageValue(totalByState, flowStates.all),
        to: `?flowState=${flowStates.all}`,
        disabled: false,
        tabValue: flowStates.all,
      },
    ],
    [totalByState],
  );

  useEffect(() => {
    const defaultTabLink = tabs[0]?.to;
    if (!flowState && defaultTabLink && !category1) {
      // replace (not push) — this default-tab redirect is transient; a history
      // entry for the flowState-less URL would break the browser Back button
      // and add a second LOCATION_CHANGE right after landing on the workspace.
      navigate(defaultTabLink, { replace: true });
    }
  }, [flowState, navigate, tabs, category1]);

  useEffect(() => {
    setShowGrid(Boolean(flowState || category1));
  }, [flowState, category1, setShowGrid]);

  if (category1) {
    return null;
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', m: 3, ml: 6.25 }}>
      <Tabs value={flowState ?? flowStates.paymentPreparation} tabs={tabs} title="" />
    </Box>
  );
};

export default PaymentFlowStates;
